import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import _ from 'lodash';
import { UrlModel } from 'components/sharpr/url/model/UrlModel.js';
import { ensure } from 'components/sharpr/ensure/ensure';

export function TagManagerRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.TagManager', {
		url: '/admin/tags/:level',
		controllerAs: 'vm',
		controller: TagManagerCtrl,
		templateUrl: '/ng/hub/Tag/Manager/TagManagerCtrl.html?' + window.BUILD,
		permission: ['manage_tags'],
		resolve: {
			style: () => AssetManager.css('/ng/hub/Tag/Manager/TagManagerCtrl.css'),
			$title: () => 'Manage Tags',
		},
		onExit: Taxonomy => {
			'ngInject';
			Taxonomy.clearTags();
		},
	});
}

export class TagManagerCtrl {
	constructor($scope, Api, Cfg, Identity, notifications, modal, $q, UrlData, $stateParams) {
		'ngInject';
		// save injections
		this.Api = Api;
		this.Cfg = Cfg;
		this.Identity = Identity;
		this.notifications = notifications;
		this.modal = modal;
		this.$q = $q;
		// setup
		this.mergeMode = false;
		this.tagsToMerge = [];
		this.isAccountLevel = $stateParams.level == 'account';
		this.canManageAccountTags = Identity.can('manage_account_tags');
		this.apiHeaders = this.isAccountLevel ? { Hubs: 'account' } : {};
		this.enforceGlobal = Cfg.read('Features.global_tags') === '1';
		this.visualTagsEnabled = Cfg.read('Features.visual_tags') === '1' && this.isAccountLevel;
		this.visualTagsLabel = Cfg.read('Features.visual_tags_label');
		if (this.isAccountLevel && this.visualTagsEnabled) {
			this.loadVisualTagsTaxonomy();
		}
		this.isReadOnly = this.enforceGlobal && !this.isAccountLevel;
		// initial load of tags
		this.tagCountCache = {};
		this.page = 1;
		this.numPages = 0;
		this.term = undefined;
		this.loadTags();
	}

	$onInit() {
		this.allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg'];
	}

	/**
	 * Load types and categories of visual tags for when you edit a visual tag
	 */
	loadVisualTagsTaxonomy() {
		this.Api.get('/visual_tags/types').then(types => {
			this.visualTagTypes = types.map(type => {
				return {
					value: type,
					text: type,
				};
			});
		});
		this.Api.get('/visual_tags/categories').then(cats => {
			this.visualTagCategories = cats.map(cat => {
				return {
					value: cat,
					text: cat,
				};
			});
		});
	}

	/**
	 * Load tags for the current page and search term
	 */
	loadTags() {
		const params = {
			term: this.term || undefined,
			limit: 10,
			page: this.page || 1,
			is_global: this.globalFilter,
		};
		this.tags = [];
		this.numPages = 1;
		this.isLoading = true;
		this.Api.getPaginated('/tags/manage', params, this.apiHeaders).then(resp => {
			this.tags = ensure.array(resp.data);
			this.numPages = resp.numPages;
			if (params.page !== this.page) {
				// the page was invalid and getPaginated set it back to 1
				this.page = params.page;
			}
			const idsToMerge = _.pluck(this.tagsToMerge || [], 'id');

			this.tags.forEach(tag => {
				if (this.Cfg.readAsBool('UI.use_classic_insights_page')) {
					tag.link = '/hub#/posts?' + UrlModel.stringify({ finetune: { term: '#' + tag.name } });
				} else {
					const urlParams = new URLSearchParams();
					urlParams.set('term', `#${tag.name}`);
					tag.link = `${window.BASE_URL}/core/insights?` + urlParams.toString();
				}
				if (idsToMerge.indexOf(tag.id) > -1) {
					tag.toMerge = true;
				}
				tag.posts = '-';
			});
			this.isLoading = false;
			this.loadCounts();
		});
	}

	/**
	 * Load the post counts for all the tags that are loaded
	 * Maintain a cache of counts to reduce API calls
	 */
	loadCounts() {
		const idsToFetch = [];
		this.tags.forEach(tag => {
			if (tag.id in this.tagCountCache) {
				tag.posts = this.tagCountCache[tag.id];
			} else {
				idsToFetch.push(tag.id);
			}
		});
		if (idsToFetch.length === 0) {
			return;
		}
		const ids = idsToFetch.join(',');
		const headers = { ...this.apiHeaders, avoidLoadingBar: true };
		this.Api.get('/tags/manage/counts', { ids }, headers).then(countsById => {
			this.tags.forEach(tag => {
				if (tag.posts === '-') {
					// set the count on the tag object
					tag.posts = countsById[tag.id] || 0;
					// save to cache
					this.tagCountCache[tag.id] = tag.posts;
				}
			});
		});
	}

	/**
	 * Setup the add/edit form on the popup
	 * @param {Object} tag  The tag to edit
	 */
	editTag(tag) {
		if (this.isReadOnly) {
			return;
		}
		tag.new_name = tag.visual_tag ? tag.visual_tag.tag_name : tag.name;
		tag.new_is_global = tag.is_global;
		this.origTag = {
			name: tag.new_name,
			is_global: tag.new_is_global,
		};
		this.activeTag = tag;
		this.editMode = true;
		if (tag.visual_tag) {
			this.origVtag = _.cloneDeep(tag.visual_tag);
			this.visualTag = tag.visual_tag;
			// set icon files
			this.smallIconFiles = [
				{
					href: this.visualTag.small_image,
				},
			];
			this.largeIconFiles = [
				{
					href: this.visualTag.large_image,
				},
			];
		} else {
			this.visualTag = {
				type: '',
				category: '',
			};
			this.origVtag = {
				type: '',
				category: '',
			};
			this.smallIconFiles = [];
			this.largeIconFiles = [];
		}
	}

	/**
	 * Save changes on the add/edit screen
	 */
	saveTag() {
		// validate
		const tag = this.activeTag;
		const vtag = this.visualTag;
		const invalidMessage = this.runValidation();
		if (invalidMessage) {
			this.modal.alert({ text: invalidMessage });
			return;
		}
		if (vtag && this.smallIconFiles && this.smallIconFiles[0]) {
			vtag.small_image = this.smallIconFiles[0].href;
		}
		if (vtag && this.largeIconFiles && this.largeIconFiles[0]) {
			vtag.large_image = this.largeIconFiles[0].href;
		}
		// save tag and vtag data; possibly using multiple API calls
		this.isSaving = true;
		const saves = [];
		// add/update tag record
		const newTag = {
			name: tag.new_name,
			is_global: tag.new_is_global,
		};
		// save for all hubs in account or just this hub
		if (tag.id) {
			// edit
			saves.push(this.Api.patch(`/tags/${tag.id}`, newTag, this.apiHeaders));
		} else {
			// add
			saves.push(this.Api.post(`/tags`, newTag, this.apiHeaders));
		}
		// save visual tag changes if any
		if (tag.is_visual && vtag && vtag.id) {
			// update visual tag
			vtag.tag_name = tag.new_name;
			saves.push(this.Api.patchDifference(`/visual_tags/${vtag.id}`, this.origVtag, vtag));
		} else if (tag.is_visual && vtag && !vtag.id) {
			// create visual tag
			vtag.tag_name = tag.new_name;
			saves.push(this.Api.post('/visual_tags', vtag));
		} else if (!tag.is_visual && vtag && vtag.id) {
			// delete visual tag
			saves.push(this.Api.delete(`/visual_tags/${vtag.id}`));
		}
		// wait for all API calls to finish
		return this.$q.all(saves).then(
			() => {
				// everything saved ok
				this.editMode = false;
				this.isSaving = false;
				this.loadTags();
			},
			() => {
				// error saving
				this.isSaving = false;
				this.modal.alert({ text: 'Server error saving tag.' });
			}
		);
	}

	/**
	 * Check that all add/edit values are valid
	 * @return {string}
	 */
	runValidation() {
		return this.runTagValidation() || this.runVtagValidation();
	}

	/**
	 * Check if all tag add/edit values are valid
	 * @return {string}
	 */
	runTagValidation() {
		if (!this.activeTag.new_name) {
			return 'Tag must have a name.';
		}
	}

	/**
	 * Check if all visual tag tag add/edit values are valid
	 * @return {string}
	 */
	runVtagValidation() {
		if (!this.activeTag.is_visual) {
			return '';
		}
		if (
			!this.largeIconFiles[0] ||
			!this.largeIconFiles[0].href ||
			!this.smallIconFiles[0] ||
			!this.smallIconFiles[0].href
		) {
			return 'Small and Large icon images are required.';
		} else if (!this.visualTag.label || !this.visualTag.category || !this.visualTag.type) {
			return 'Label, type, and category are required.';
		}
	}

	/**
	 * Show the settings popup
	 */
	popupTagSettings() {
		this.showTagSettings = true;
		this.settings = {
			global: this.enforceGlobal,
			visual: this.visualTagsEnabled,
		};
	}

	/**
	 * Handle the "save" button on the settings popup
	 */
	saveSettings() {
		if (this.settings.global != this.enforceGlobal) {
			const newValue = this.settings.global ? 1 : 0;
			this.Cfg.write('Features.global_tags', newValue, 'affiliation');
		}
		if (this.settings.visual != this.visualTagsEnabled) {
			const newValue = this.settings.visual ? 1 : 0;
			this.Cfg.write('Features.visual_tags', newValue, 'affiliation');
			if (this.settings.visual) {
				this.loadVisualTagsTaxonomy();
			}
		}
		this.enforceGlobal = this.settings.global;
		this.visualTagsEnabled = this.settings.visual;
		this.showTagSettings = false;
	}

	/**
	 * Set a tag's "is_global" property
	 * @param {Object} tag
	 * @return {Promise}
	 */
	toggleGlobal(tag) {
		tag.isSaving = true;
		const done = () => (tag.isSaving = false);
		const data = {
			is_active: true,
			is_global: !!tag.is_global,
		};
		const headers = this.isAccountLevel ? { Hubs: 'account' } : {};
		return this.Api.patch(`/tags/${tag.id}`, data, headers).then(done, done);
	}

	/**
	 * Confirm that the user really wants to delete the given tag
	 * @param {Object} tag
	 */
	confirmDelete(tag) {
		const yesDelete = [];
		yesDelete.push(`
			Are you sure you want to delete the tag <b>${_.escape(tag.name)}</b>?
		`);
		if (tag.posts > 0) {
			yesDelete.push(`
				This tag will remain associated with ${tag.posts} post(s)
				but will no longer show up as a suggestion
			`);
		}
		yesDelete.push(`
			If this tag is manually entered on a post in the future, it will be
			re-added.
		`);
		const confirmText = yesDelete.join('<br><br>');
		this.modal.confirm({ escapedHtml: confirmText, width: 300 }).then(() => {
			this.isSaving = true;
			this.deleteTag(tag).then(() => {
				this.editMode = false;
				this.isSaving = false;
				this.loadTags();
			});
		});
	}

	/**
	 * Use the API to deactivate the given tag
	 * @param {Object} tag
	 * @return {Promise}
	 */
	deleteTag(tag) {
		const done = () => this.loadTags();
		const data = {
			is_active: false,
		};
		const headers = this.isAccountLevel ? { Hubs: 'account' } : {};
		return this.Api.patch(`/tags/${tag.id}`, data, headers).then(done, done);
	}

	/**
	 * Initialize merge mode
	 * @param tag
	 */
	startMerge(tag) {
		this.editMode = false;
		this.mergeMode = true;
		tag.toMerge = false;
		this.addMergeTag(tag);
	}

	/**
	 * Add the given tag to the list of tags to merge
	 * @param tag
	 */
	addMergeTag(tag) {
		tag.toMerge = true;
		this.tagsToMerge.push(tag);
		this.toMergeName = this.toMergeName || tag.name;
	}

	/**
	 * Remove the given tag from the list of tags to merge
	 * @param tag
	 */
	removeMergeTag(tag) {
		const idx = this.tagsToMerge.indexOf(tag);
		if (idx > -1) {
			tag.toMerge = false;
			this.tagsToMerge.splice(idx, 1);
		}
	}

	/**
	 * Perform the merge requested by the user
	 */
	mergeTags() {
		const numToMerge = this.tagsToMerge.length;
		// 2 or more tags are required to merge
		if (numToMerge < 2) {
			this.modal.alert({ text: 'Please choose 2 or more tags to merge.' });
			return;
		}
		const message = `Are you sure you want to merge these ${numToMerge} tags?<br><br>This cannot be undone.`;
		this.modal.confirm({ escapedHtml: message }).then(() => this._doMerge());
	}

	/**
	 * Hit the /v2/tags/merge endpoint
	 * @private
	 */
	_doMerge() {
		// get ready for api post
		let data = {
			// IDs of every tag we want to merge
			retire_names: _.pluck(this.tagsToMerge, 'name'),
			// the merged name
			new_name: this.toMergeName,
			// if we want the new tag to be global
			is_global: !!this.toMergeGlobal,
		};
		// this endpoint will actually create a new tag first
		// then change all uses of any of the chosen IDs to that new ID
		this.Api.post('/tags/merge', data, this.apiHeaders).then(
			() => {
				this._mergedOk();
			},
			() => {
				this.modal.alert({ text: 'Server error starting merge. Try reloading the page.' });
			}
		);
	}

	/**
	 * Let user know that /v2/tags/merge responded with 202
	 * @private
	 */
	_mergedOk() {
		// let user know what is happening
		const numPosts = _.sum(_.pluck(this.tagsToMerge, 'posts'));
		let msg;
		if (numPosts > 10000) {
			msg = 'Please check back in a few minutes.';
		} else {
			msg = 'Please wait a few moments.';
		}
		const refresh = () => {
			this.tags = [];
			this.isLoading = true;
			this.loadTags();
		};
		const s = numPosts === 1 ? '' : 's';
		const mergeInfo = `
			This merge affects ${numPosts} post${s}. ${msg}
			<br><br>
			You may close this message at any time.
			You won't automatically see changes on the list below.
		`;
		this.modal.alert({ escapedHtml: mergeInfo }).then(refresh, refresh);
		// update counts
		this.tagsToMerge.forEach(tag => (tag.posts = '*'));
		// update UI elements temporarily until tag data comes back from api and we reload the tag list
		this.tagsToMerge = [];
		this.mergeMode = false;
		this.toMergeName = '';
		this.toMergeGlobal = false;
	}

	/**
	 * Prompt user for a new visual tag type
	 */
	addVisualTagType() {
		this.modal.prompt({ text: 'Enter new type name:' }).then(
			name => {
				const newType = { value: name, text: name };
				this.visualTagTypes = [...this.visualTagTypes, newType];
				this.visualTag.type = name;
			},
			() => {}
		);
	}

	/**
	 * Prompt user for a new visual tag category
	 */
	addVisualTagCategory() {
		this.modal.prompt({ text: 'Enter new category name:' }).then(
			name => {
				const newType = { value: name, text: name };
				this.visualTagCategories = [...this.visualTagCategories, newType];
				this.visualTag.category = name;
			},
			() => {}
		);
	}
}
